import { normalizeDateOnly } from '@/lib/dates';

interface BookingSlotFields {
  date: string;
  time: string;
  venueId: string;
}

/**
 * True when the draft moves the booking to a different slot — another date,
 * time, or venue. Only slot moves need an available slot to save against.
 * Service and add-on changes deliberately do NOT count: amending the service
 * (e.g. SCW medical -> D&A-only) keeps the existing slot and must stay
 * savable even when that slot has passed or the day is fully booked — the
 * server skips schedule/capacity validation for slot-preserving edits too.
 * Status, PO, notes and customer detail changes are not slot moves either.
 */
export const hasBookingScheduleChange = (
  original: BookingSlotFields,
  draft: BookingSlotFields
): boolean =>
  normalizeDateOnly(original.date) !== normalizeDateOnly(draft.date) ||
  original.time !== draft.time ||
  original.venueId !== draft.venueId;

/**
 * Save gate for the staff booking editor. Availability only blocks saving
 * when the slot actually moved — otherwise a status-only or service-only
 * edit (e.g. marking a past appointment Completed after the day's slots have
 * gone) would be impossible. Cancellation ignores schedule/availability
 * entirely; a required-but-missing PO always blocks.
 */
export const shouldDisableBookingSave = ({
  isLoadingAvailability,
  poMissing,
  isCancelling,
  scheduleChanged,
  timeMissing,
  hasAvailabilityData,
  hasAvailableSlots,
  allowOverbook
}: {
  isLoadingAvailability: boolean;
  poMissing: boolean;
  isCancelling: boolean;
  scheduleChanged: boolean;
  timeMissing: boolean;
  hasAvailabilityData: boolean;
  hasAvailableSlots: boolean;
  allowOverbook: boolean;
}): boolean =>
  poMissing ||
  (!isCancelling &&
    scheduleChanged &&
    (isLoadingAvailability ||
      timeMissing ||
      (hasAvailabilityData && !hasAvailableSlots && !allowOverbook)));
